import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Home, Calendar } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paymentId = searchParams.get('paymentId'); // PayPal
  const payerId = searchParams.get('PayerID'); // PayPal
  const provider = searchParams.get('provider') || 'stripe';
  
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(null);
  const attemptsRef = useRef(0);

  // Handle PayPal payment execution
  const executePayPalPayment = useCallback(async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/payments/paypal/execute?paymentId=${paymentId}&PayerID=${payerId}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to execute PayPal payment');
      }

      const data = await response.json();
      
      if (data.success) {
        setPaymentDetails({
          payment_status: 'paid',
          provider: 'paypal',
          ...data.transaction
        });
        setStatus('success');
      } else {
        throw new Error('PayPal payment failed');
      }
    } catch (err) {
      console.error('PayPal execution error:', err);
      setStatus('error');
      setError(err.message);
    }
  }, [paymentId, payerId]);

  // Handle Stripe payment status polling
  const pollStripeStatus = useCallback(async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attemptsRef.current >= maxAttempts) {
      setStatus('timeout');
      setError('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/checkout/status/${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      setPaymentDetails(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling if still pending
      attemptsRef.current += 1;
      setTimeout(pollStripeStatus, pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setStatus('error');
      setError(err.message);
    }
  }, [sessionId]);

  useEffect(() => {
    // Handle PayPal return
    if (provider === 'paypal' && paymentId && payerId) {
      executePayPalPayment();
    }
    // Handle Stripe return
    else if (sessionId) {
      attemptsRef.current = 0;
      pollStripeStatus();
    } else {
      setStatus('error');
      setError('No payment information provided');
    }
  }, [sessionId, paymentId, payerId, provider, executePayPalPayment, pollStripeStatus]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Card className="shadow-lg">
          <CardHeader className="text-center pb-2">
            {status === 'checking' && (
              <div className="flex flex-col items-center">
                <Loader2 className="h-16 w-16 text-primary animate-spin mb-4" />
                <CardTitle className="text-2xl">Processing Payment...</CardTitle>
              </div>
            )}
            
            {status === 'success' && (
              <div className="flex flex-col items-center">
                <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
                <CardTitle className="text-2xl text-green-700">Payment Successful!</CardTitle>
              </div>
            )}
            
            {(status === 'error' || status === 'expired' || status === 'timeout') && (
              <div className="flex flex-col items-center">
                <XCircle className="h-16 w-16 text-red-500 mb-4" />
                <CardTitle className="text-2xl text-red-700">
                  {status === 'expired' ? 'Payment Expired' : 'Payment Issue'}
                </CardTitle>
              </div>
            )}
          </CardHeader>
          
          <CardContent className="text-center">
            {status === 'checking' && (
              <p className="text-gray-600">
                Please wait while we confirm your payment...
              </p>
            )}
            
            {status === 'success' && paymentDetails && (
              <div className="space-y-4">
                <p className="text-gray-600">
                  Thank you for your purchase! Your payment has been processed successfully.
                </p>
                
                <div className="bg-gray-50 rounded-lg p-4 text-left">
                  <h3 className="font-semibold mb-2">Payment Details</h3>
                  <div className="space-y-1 text-sm">
                    {/* Handle both Stripe and PayPal formats */}
                    {paymentDetails.amount_total && (
                      <p><span className="text-gray-500">Amount:</span> ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}</p>
                    )}
                    {paymentDetails.amount && !paymentDetails.amount_total && (
                      <p><span className="text-gray-500">Amount:</span> ${paymentDetails.amount.toFixed(2)} USD</p>
                    )}
                    {paymentDetails.package_name && (
                      <p><span className="text-gray-500">Package:</span> {paymentDetails.package_name}</p>
                    )}
                    {paymentDetails.metadata?.package_name && (
                      <p><span className="text-gray-500">Package:</span> {paymentDetails.metadata.package_name}</p>
                    )}
                    {paymentDetails.metadata?.treatment_name && (
                      <p><span className="text-gray-500">Treatment:</span> {paymentDetails.metadata.treatment_name}</p>
                    )}
                    {paymentDetails.credits && (
                      <p><span className="text-gray-500">Credits Added:</span> {paymentDetails.credits}</p>
                    )}
                    {paymentDetails.metadata?.credits && (
                      <p><span className="text-gray-500">Credits Added:</span> {paymentDetails.metadata.credits}</p>
                    )}
                    {paymentDetails.provider && (
                      <p><span className="text-gray-500">Paid via:</span> {paymentDetails.provider === 'paypal' ? 'PayPal' : 'Card'}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                  <Button asChild>
                    <Link to="/">
                      <Home className="h-4 w-4 mr-2" />
                      Go Home
                    </Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <a href="#book-now">
                      <Calendar className="h-4 w-4 mr-2" />
                      Book Appointment
                    </a>
                  </Button>
                </div>
              </div>
            )}
            
            {(status === 'error' || status === 'expired' || status === 'timeout') && (
              <div className="space-y-4">
                <p className="text-gray-600">
                  {error || 'There was an issue processing your payment. Please try again or contact support.'}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                  <Button asChild>
                    <Link to="/memberships">Try Again</Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/help">Contact Support</Link>
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
